import { useSearchParams } from 'react-router-dom'
import { compact, isEmpty } from 'lodash'
import { useEffect, useMemo, useState } from 'react'
import { FilterObject, FilterValueType, decodeFilters, encodeFilters } from 'app/filter.utils'
import { useImmediateRequest } from 'app/hooks/useImmediateRequest'
import { SearchResponse } from 'app/services/search'
import { useHandleSearchParams } from 'app/utils'
import { bg } from 'lib/background'
import { logError } from 'lib/logging'
import { OptionType } from 'types/OptionType'

interface UseBaseFilterArgs {
  attribute: string
  displayName: string
  queryURL?: string
  fetcher?: (params: { query: string; [key: string]: any }) => Promise<any>
  preAppliedValues?: string | string[]
  customSearchParam: string
  defaultFilters?: FilterObject[]
}

export type UseBaseFilterReturn = {
  selectedItems: string[]
  totalCount: number | null
  handleChange: (selectedOptions: OptionType[]) => void
  attribute: string
  displayName: string
  queryURL?: string
  fetcher?: (params: { query: string; [key: string]: any }) => Promise<any>
}

export function useBaseFilter({
  attribute,
  displayName,
  queryURL,
  fetcher,
  preAppliedValues,
  customSearchParam,
  defaultFilters,
}: UseBaseFilterArgs): UseBaseFilterReturn {
  const [searchParams] = useSearchParams()
  const handleSearchParams = useHandleSearchParams()
  const [selectedItems, setSelectedItems] = useState<string[]>(compact(preAppliedValues))

  // Use useImmediateRequest for REST URLs, or fetcher for GraphQL
  const [restResponse, restError] = useImmediateRequest<SearchResponse<Record<string, unknown>>>(queryURL ?? ``)
  const [fetcherResponse, setFetcherResponse] = useState<SearchResponse<Record<string, unknown>>>()
  const [fetcherError, setFetcherError] = useState<any>()

  useEffect(() => {
    if (!fetcher) return
    bg(async () => {
      try {
        const result = await fetcher({ query: `` })
        setFetcherResponse(result as SearchResponse<Record<string, unknown>>)
      } catch (err: unknown) {
        logError(`useBaseFilter: failed to fetch filter options`, err)
        setFetcherError(err)
      }
    })
  }, [])

  const response = fetcher ? fetcherResponse : restResponse
  const responseError = fetcher ? fetcherError : restError

  // Synchronize selectedItems with URL state
  useEffect(() => {
    // If URL has filters, sync with them
    if (searchParams.has(customSearchParam)) {
      const currentFilters: FilterObject[] = decodeFilters(searchParams.get(customSearchParam))
      const currentFilter = currentFilters.find((filter) => filter.attribute === attribute)

      if (currentFilter) {
        const urlValues = Array.isArray(currentFilter.value) ? currentFilter.value : [currentFilter.value]
        setSelectedItems(urlValues.filter(Boolean) as string[])
      } else {
        setSelectedItems([])
      }
    }
  }, [searchParams, customSearchParam, attribute])

  const totalCount = useMemo(() => {
    if (!isEmpty(responseError)) {
      logError(`Error fetching total count`, responseError)
      return null
    }
    return response?.total || 0
  }, [response, responseError])

  const handleChange = (selectedOptions: OptionType[]) => {
    const isSelectAll = selectedOptions.length === 1 && selectedOptions[0].id === `0`
    const currentFilters: FilterObject[] = decodeFilters(searchParams.get(customSearchParam))
    const baseFilters = currentFilters.length > 0 ? currentFilters : defaultFilters || []
    const otherFilters = baseFilters.filter((f) => f.attribute !== attribute)
    if (isSelectAll) {
      setSelectedItems([])
    } else {
      setSelectedItems(selectedOptions.map((option) => option.value))
    }
    const filterObject: FilterObject = {
      attribute,
      displayName,
      operator: isSelectAll ? `attribute_exists` : `in`,
      value: isSelectAll ? [] : selectedOptions.map((option) => option.value),
      type: FilterValueType.id,
    }
    const newFilters = [...otherFilters, filterObject]
    handleSearchParams({ [customSearchParam]: encodeFilters(newFilters) })
  }

  return {
    selectedItems,
    totalCount,
    handleChange,
    attribute,
    displayName,
    queryURL,
    fetcher,
  }
}
